/**
 * Utility functions for AI Chat Assistant
 */

import type { MarkedOptions } from "marked";
import { ACTION_CONFIGS, SHOW_TARGETS } from "./types";

// Exported directly (not via a getter) so markdownRenderer.ts's import
// reflects the value loadMarked() assigns below - ES module bindings are
// live references, not snapshots taken at import time.
export let markedInstance: typeof import("marked").marked | null = null;
let markedLoading: Promise<typeof import("marked")> | null = null;

/**
 * Lazy loads the marked library for markdown rendering
 */
export const loadMarked = async () => {
  if (markedInstance) return markedInstance;
  if (!markedLoading) {
    markedLoading = import("marked");
  }
  const { marked } = await markedLoading;
  marked.setOptions({
    breaks: true,
    gfm: true,
  } as MarkedOptions);
  markedInstance = marked;
  return marked;
};

/**
 * Parses action buttons from assistant response content
 */
// The prompt shows the tag in backticks, so the model sometimes wraps it in them
const ACTION_TAG = /`?\[ACTIONS:\s*([^\]]*)\]`?/;

export const parseActionsFromContent = (
  content: string
): { cleanContent: string; actions: string[] } => {
  const match = content.match(ACTION_TAG);
  // Drop names without a button so an unknown-only tag still gets the keyword fallback
  const actions = match
    ? match[1].split(",").map((a) => a.trim()).filter((a) => a in ACTION_CONFIGS)
    : [];
  const cleanContent = content
    .replace(ACTION_TAG, "")
    .replace(/`?\[ACTIONS:[^\]]*$/, "") // unterminated tag from a cut-off reply
    .trim();
  return { cleanContent, actions };
};

const SHOW_TAG = /`?\[SHOW:\s*([a-z-]+)\s*\]`?/i;

/**
 * Pulls the optional [SHOW: target] spotlight marker out of a reply
 */
export const parseShowFromContent = (
  content: string
): { cleanContent: string; show?: string } => {
  const id = content.match(SHOW_TAG)?.[1].toLowerCase();
  const cleanContent = content
    .replace(SHOW_TAG, "")
    .replace(/`?\[SHOW:[^\]]*$/, "") // unterminated tag from a cut-off reply
    .trim();
  return { cleanContent, show: id && id in SHOW_TARGETS ? id : undefined };
};

// ponytail: first keyword match wins; only offers the button, never auto-scrolls
const SHOW_KEYWORDS: [RegExp, string][] = [
  [/inference|llama|tokens?\/s|gpu|local (llm|model)/i, "inference-engine"],
  [/hermes|eval|agent platform/i, "hermes"],
  [/pi-?cloud|raspberry|firewall|homelab|self-host/i, "pi-cloud"],
  [/screen.?reader|accessib|wcag|a11y/i, "screen-reader-demo"],
  [/american express|amex|overview|personaliz/i, "amex-overview"],
  [/mentor|codepath|teach/i, "codepath"],
];

export const detectShowFromQuestion = (question: string) =>
  SHOW_KEYWORDS.find(([re]) => re.test(question))?.[1];

export const SPOTLIGHT_EVENT = "chat:spotlight";

/**
 * Spotlights a homepage element, or loads the homepage with ?show= when the
 * element isn't on the current page
 */
export const showOnPage = (target: string, focus = false) => {
  if (document.querySelector(`[data-chat-target="${target}"]`)) {
    window.dispatchEvent(new CustomEvent(SPOTLIGHT_EVENT, { detail: { target, focus } }));
  } else {
    window.location.href = `/?show=${target}`;
  }
};

/**
 * Detects relevant actions based on user question
 * Used as fallback when AI doesn't include action markers
 */
export const detectActionsFromQuestion = (question: string): string[] => {
  const lowerQuestion = question.toLowerCase();
  const actions: string[] = [];

  // Contact-related
  if (/contact|reach|email|hire|work with|get in touch/i.test(lowerQuestion)) {
    actions.push("contact_form", "send_email");
  }

  // Resume/CV
  if (/resume|cv|curriculum/i.test(lowerQuestion)) {
    actions.push("view_resume");
  }

  // Projects
  if (/project|portfolio|built|created|github/i.test(lowerQuestion)) {
    actions.push("view_projects", "view_github");
  }

  // Travel/Photos
  if (/travel|photo|gallery|trip|country|visit/i.test(lowerQuestion)) {
    actions.push("view_travel");
  }

  // Experience/Work
  if (/experience|work|job|career|american express|amex/i.test(lowerQuestion)) {
    actions.push("view_experience", "view_resume");
  }

  // Skills/Tech
  if (/skill|tech|stack|react|javascript|typescript/i.test(lowerQuestion)) {
    actions.push("view_projects");
  }

  // Social/Online presence
  if (/linkedin|social|online|find you/i.test(lowerQuestion)) {
    actions.push("view_linkedin", "view_github");
  }

  // Snake game
  if (/snake|game|play/i.test(lowerQuestion)) {
    actions.push("play_snake");
  }

  // Features/Portfolio questions (like the one in the screenshot)
  if (/feature|unique|special|what.*(have|does|include)|portfolio/i.test(lowerQuestion)) {
    actions.push("view_projects", "view_travel", "play_snake");
  }

  // Complex questions - offer to ask directly
  if (/help|assist|question about|specific|complex/i.test(lowerQuestion) && actions.length === 0) {
    actions.push("ask_directly");
  }

  return [...new Set(actions)]; // Remove duplicates
};

/**
 * Formats timestamp as relative time (e.g., "Just now", "2 min ago")
 */
export const formatRelativeTime = (timestamp: number): string => {
  const now = Date.now();
  const diff = now - timestamp;
  const seconds = Math.floor(diff / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  if (seconds < 10) return "Just now";
  if (seconds < 60) return `${seconds} sec ago`;
  if (minutes < 60) return `${minutes} min ago`;
  if (hours < 24)
    return `${hours} hour${hours > 1 ? "s" : ""} ago`;
  return `${days} day${days > 1 ? "s" : ""} ago`;
};

/**
 * Handles action button clicks (navigation, scrolling, external links)
 * @param action - The action identifier
 * @param onClose - Callback to close the chat window
 */
export const handleAction = (action: string, onClose?: () => void) => {
  switch (action) {
    case "view_resume":
      window.open("/andrew-alagna-resume.pdf", "_blank");
      break;
    case "view_linkedin":
      window.open("https://www.linkedin.com/in/andrew-a-10b88215b/", "_blank");
      break;
    case "view_github":
      window.open("https://github.com/elchic00", "_blank");
      break;
    case "contact_form":
      onClose?.();
      navigateToSection("contact");
      break;
    case "ask_directly":
      onClose?.();
      navigateToSection("contact");
      break;
    case "view_projects":
      window.location.href = "/projects";
      onClose?.();
      break;
    case "view_travel":
      window.location.href = "/travel";
      onClose?.();
      break;
    case "view_experience":
      onClose?.();
      navigateToSection("experience");
      break;
    case "play_snake":
      window.location.href = "/snake";
      onClose?.();
      break;
    case "send_email":
      window.location.href = "mailto:aalagna04@gmail.com";
      break;
  }
};

/**
 * Navigate to a section, handling both same-page and cross-page navigation
 */
const navigateToSection = (sectionId: string) => {
  const section = document.getElementById(sectionId);
  if (section) {
    // Section exists on current page - scroll to it
    setTimeout(() => {
      section.scrollIntoView({ behavior: "smooth" });
    }, 300);
  } else {
    // Section doesn't exist - navigate to home page with hash
    // Use /#section format for BrowserRouter compatibility
    window.location.href = `/#${sectionId}`;
  }
};
